import cloneDeep from 'lodash/cloneDeep';
import 'reflect-metadata';
import { 
    IJsonMetaData, 
    JSONPROPERTY__METADATA_KEY, 
    METADATA_SERIALIZE_TYPES
} from '../models/utilities/json-property';
import { PagedApiResult, PageData } from '../models/utilities/paged-request';
import { AppUtils } from './app-utils';

/**
 * Utility class for handling deserialization of JSON objects
 * to their TS counterpart. 
 */
export class Mapper {

    /**
     * Serialize a JSON object to a typed TS class
     * Currently used for specially formatting client data 
     * for the server.  (Datetimes without timezones now)
     * @param fromObj The TS class to serialize to
     */
    public static serialize<T>(fromObj: T, fromType? : { new(): any }) {

        if (fromObj === undefined) {
            return undefined;
        }

        // Make a copy so we don't impact the rest of the data
        // and use that as the initial keyset
        const objCopy = cloneDeep(fromObj);
        let keySet = objCopy;

        // For nested list serialization, the fromType is populated
        // so we can get the expected metadata off it.  If there's no
        // fromType we just assume fromObj is a TS object and read
        // the metadata from it
        if (fromType) {
            keySet = new fromType();
        }

        // Iterate over the keyset and pull metadata off it.  Then 
        // run through the serializer.
        Object.keys(keySet).forEach((key) => {
            const propertyMetadata = Mapper.getJsonProperty(keySet, key);
            if (propertyMetadata) {
                (objCopy as any)[key] = Mapper.convertFromClass(propertyMetadata, key, objCopy);
            }
        });

        return objCopy;
    }

    /**
     * Deserialize a JSON object to a typed TS class
     * @param toClass The TS class to deserialize to
     * @param jsonObject The raw JSON object
     */
    public static deserialize<T>(toClass: { new(): T }, jsonObject: any) {

        if ((toClass === undefined) || (jsonObject === undefined)) {
            return undefined;
        }

        const obj : any = new toClass();

        Object.keys(obj).forEach((key) => {

            const propertyMetadata = Mapper.getJsonProperty(obj, key);
            if (propertyMetadata) {
                obj[key] = Mapper.convertToClass(propertyMetadata, obj, key, jsonObject);
            } 
            else if (jsonObject && jsonObject[key] !== undefined) {
                obj[key] = jsonObject[key];
            }
            
        });

        return obj;
    }

    /**
     * Deserialize a JSON object to a typed TS class
     * @param toClass The TS class to deserialize to
     * @param jsonObject The raw JSON object
     */
    public static deserializeList<T>(toClass: { new(): T }, jsonObject: any) {

        if ((toClass === undefined) || (jsonObject === undefined)) {
            return undefined;
        }

        var srcItems = jsonObject.items ? jsonObject.items : jsonObject;

        const items = [] as T[];
        srcItems.forEach((curr: any) => {
        
            const obj : any = new toClass();
            Object.keys(obj).forEach((key) => {

                const propertyMetadata = Mapper.getJsonProperty(obj, key);
                if (propertyMetadata) {
                    obj[key] = Mapper.convertToClass(propertyMetadata, obj, key, curr);
                } 
                else if (curr && curr[key] !== undefined) {
                    obj[key] = curr[key];
                }
            });
    
            items.push(obj);
        });

        return items;
    }

    /**
     * Deserialize a JSON object to a typed TS class and set paging values
     * based on passed in parameters
     * @param toClass The TS class to deserialize to
     * @param jsonObject The raw JSON object
     * @param pagingData: The paging data that was sent to the query
     */
    public static deserializeApiList<T>(toClass: { new(): T }, jsonObject: any, pagingData: PageData) : PagedApiResult<T> {

        var srcItems = jsonObject.items ? jsonObject.items : jsonObject;

        if ((toClass === undefined) || (jsonObject === undefined)) {
            return new PagedApiResult<T>();
        }

        const pagedResult = new PagedApiResult<T>();
        const items = [] as T[];
        if (!Array.isArray(srcItems)) {
            return new PagedApiResult<T>();
        }
        srcItems.forEach((curr: any) => {
        
            const obj : any = new toClass();
            Object.keys(obj).forEach((key) => {

                const propertyMetadata = Mapper.getJsonProperty(obj, key);
                if (propertyMetadata) {
                    obj[key] = Mapper.convertToClass(propertyMetadata, obj, key, curr);
                } 
                else if (curr && curr[key] !== undefined) {
                    obj[key] = curr[key];
                }
            });
    
            items.push(obj);
        });

        if (items) {            
            if (jsonObject.totalItems) {
                pagingData.totalItems = jsonObject.totalItems;
                pagingData.totalPages = Math.ceil(jsonObject.totalItems / pagingData.take);
                pagingData.isEnd = pagingData.pageCount >= pagingData.totalPages;
            }
            else {
                pagingData.isEnd = (items.length < pagingData.take) ? true : false;
            }
            

            pagedResult.results = items;
            pagedResult.pagination = pagingData;
        }

        return pagedResult;
    }

    /**
     * Convert the given jsonObject into a typed/converted object
     * @param propMetadata The property metadata specified on the model
     * @param obj The new class instance we're populating
     * @param key The property name we're iterating on
     * @param jsonObject The raw JSON data
     */
    private static convertToClass(propMetadata: any, obj: any, key: string, jsonObject: any) {

        const propertyName = propMetadata.name || key;
        const propertyType = propMetadata.toType || undefined;
        const propertyDesignType = propMetadata.designType || undefined;
        const enumMapper            = propMetadata.enumMapper || undefined;
        const enumProp              = propMetadata.enumProp || undefined;
        const innerJson = jsonObject ? jsonObject[propertyName] : undefined;
        const toClass = propMetadata.toClass; // AppUtils.getToClass(obj, key);

        if (AppUtils.isArray(toClass) || propertyDesignType === 'array') {

            const metadata = Mapper.getJsonProperty(obj, key);
            if (metadata.toClass || AppUtils.isPrimitive(toClass)) {
                if (innerJson && AppUtils.isArray(innerJson)) {
                    return innerJson.map(
                        (item: any) => Mapper.deserialize(metadata.toClass!, item)
                    );
                } else {
                    return undefined;
                }
            }
            else if (enumMapper) {
                if (innerJson && AppUtils.isArray(innerJson)) {
                    return innerJson.map((item: any) => {
                        const sClass = enumMapper(item[enumProp]);
                        return Mapper.deserialize(sClass, item);
                    });
                } else {
                    return undefined;
                }
            }
            else {
                return innerJson;
            }
        }
        else if (propertyType) {
            return Mapper.convertToType(propertyType, innerJson);
        }
        else if (!AppUtils.isPrimitive(toClass)) {
            return Mapper.deserialize(toClass, innerJson);
        }
        else {
            return jsonObject ? jsonObject[propertyName] : undefined;
        }

    }

    private static convertToType(type: METADATA_SERIALIZE_TYPES, val: any) {
        switch (type) {
            case "date":
            case "datetime-specific":
                if (val) {
                    return new Date(val);
                }
                return val;
            case "datetime":
                // Due server oddities, everything should be treated as UTC
                // but they don't all carry the 'Z' indicating UTC...
                if (val && (typeof val === 'string')) {
                    const hasZ = val.indexOf('Z');
                    if (hasZ === -1) {
                        val = `${val}Z`;
                    }

                    return new Date(val);
                }
                return val;
            default:
                return val;
        }
    }

    /**
     * Convert the given jsonObject into a typed/converted object
     * @param propMetadata The property metadata specified on the model
     * @param obj The new class instance we're populating
     * @param key The property name we're iterating on
     * @param jsonObject The raw JSON data
     */
    private static convertFromClass(propMetadata: any, key: string, fromObj : any) {

        const propertyName          = propMetadata.name || key;
        const propertyType          = propMetadata.toType || undefined;
        const propertyDesignType    = propMetadata.designType || undefined;
        const enumMapper            = propMetadata.enumMapper || undefined;
        const enumProp              = propMetadata.enumProp || undefined;
        const innerJson             = fromObj ? fromObj[propertyName] : undefined;
        const toClass               = propMetadata.toClass;

        if (AppUtils.isArray(toClass) || propertyDesignType === 'array') {

            const metadata = Mapper.getJsonProperty(fromObj, key);
            if (metadata.toClass || AppUtils.isPrimitive(toClass)) {
                if (innerJson && AppUtils.isArray(innerJson)) {
                    return innerJson.map(
                        (item: any) => Mapper.serialize(item, toClass)
                    );
                } else {
                    return undefined;
                }
            }
            else if (enumMapper) {

                if (innerJson && AppUtils.isArray(innerJson)) {
                    return innerJson.map((item: any) => {
                        const sClass = enumMapper(item[enumProp]);
                        const sMap = Mapper.serialize(item, sClass);
                        return sMap;
                    });
                } else {
                    return undefined;
                }

            }
            else {
                return innerJson;
            }
        }
        else if (propertyType) {
            return Mapper.convertFromType(propertyType, innerJson);
        }
        else if (!AppUtils.isPrimitive(toClass)) {
            return Mapper.serialize(innerJson);
        }
        else {
            return fromObj ? fromObj[propertyName] : undefined;
        }

    };

    private static convertFromType(type: METADATA_SERIALIZE_TYPES, val: any) {
        switch (type) {
            case "date":
            case "datetime":
                if (val) {
                    return new Date(val);
                }
                return val;            
            case "datetime-specific":
                if (val) {
                    const year      = val.getFullYear();
                    const month     = (val.getMonth() + 1).toString().padStart(2, '0');
                    const day       = val.getDate().toString().padStart(2, '0');
                    const hours     = val.getHours().toString().padStart(2, '0');
                    const minutes   = val.getMinutes().toString().padStart(2, '0');
                    const newStr    = `${year}-${month}-${day}T${hours}:${minutes}:00.000`;
                    return newStr;
                }
                else {
                    return val;
                }
            default:
                return val;
        }
    }

    private static getJsonProperty<T>(target: any, propertyKey: string): IJsonMetaData<T> {
        return Reflect.getMetadata(JSONPROPERTY__METADATA_KEY, target, propertyKey);
    }
}
